import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useNow } from '../../hooks/useNow';
import { useCompactMotion } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { closedDaysLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { visitStatus } from '../../core/rules/visitStatus';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';

/** Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md). */
const ROOM = 'https://cdn.magicpatterns.com/patterns/generated-images/7166f87a-c402-483f-8556-b060455341f8.jpg';

/**
 * "Kom langs": het laatste blok van de homepage, op blush. Het beeld staat in een boog, als
 * een deuropening die bij het scrollen opengaat: smal → volle boog, terwijl het beeld uitzoomt.
 * Daarna komen adres en knop binnen. Bij "minder beweging" staat de boog meteen open.
 * Een kleine regel zegt wanneer je terecht kunt (`core/rules/visitStatus.ts`): vandaag, morgen
 * of op welke dag weer. Eén knop: plan een afspraak; de route is een rustige link.
 * Draagt ook de lokale vindbaarheid (stad in de kop, adres als tekst).
 * Adres en dagen komen uit Instellingen › Salon & contact en › Salondagen; de woorden en het beeld uit Paginateksten.
 */
export function HomeVisit() {
  const pages = usePages();
  const ref = useRef<HTMLDivElement>(null);
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const now = useNow();
  const copy = pages.home.visit;
  const media = mediaOr(copy.media, {
    image: ROOM,
    alt: 'Sfeerbeeld: een behandelbed met linnen in daglicht'
  });
  const {
    openingHours,
    salonExceptions
  } = useSite();
  const closed = closedDaysLabel(openingHours);
  const status = visitStatus(openingHours, salonExceptions, now);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.6], [1.15, 1]);
  // De boog opent als een deur: van smal naar zijn volle vorm, klaar als hij midden in beeld staat.
  const compact = useCompactMotion();
  const side = compact ? 14 : 28;
  const clipPath = useTransform(scrollYProgress, [0.05, 0.5], [`inset(0% ${side}% 0% ${side}% round 9999px 9999px 0 0)`, 'inset(0% 0% 0% 0% round 9999px 9999px 0 0)']);
  return <section className="bg-blush" aria-labelledby="home-visit" data-id="element-7549" data-mp="2t6dwwg7wxz2p">
      <div className="mx-auto grid max-w-content gap-12 px-6 py-20 sm:py-24 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10 lg:py-32" data-id="element-7550" data-mp="2t6vy7r9fn3hh">
        <div ref={ref} className="mx-auto w-full max-w-xs sm:max-w-sm lg:col-span-5 lg:max-w-none" data-id="element-7551" data-mp="2f0u0sz8h4jx5">
          {/* clip-path i.p.v. breedte: het beeld vervormt niet en de pagina verspringt niet. */}
          <motion.div style={reduce ? undefined : {
          clipPath
        }} className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full bg-sand" data-id="element-7552" data-mp="25b23hx8zpcoz">
            <motion.div style={reduce ? undefined : {
            scale
          }} className="absolute inset-0" data-id="element-7553" data-mp="2a8u73oizyn90">
              <MediaView media={media} sizes="(min-width: 1024px) 38vw, 100vw" className="h-full w-full" data-id="element-7554" data-mp="1vbtc9c5iec6b" />
            </motion.div>
          </motion.div>
        </div>

        {/* Adres en knop komen binnen als de boog bijna open is, licht van rechts. */}
        <motion.div className="lg:col-span-7 lg:pb-4" initial={reduce ? false : {
        opacity: 0,
        x: 24
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        amount: 0.35
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease
      }} data-id="element-7555" data-mp="1rb9i8hg8fooe">
          <p className="flex items-center gap-2.5 text-[14px] text-ink" data-id="element-7556" data-mp="3lmy12upl4be2">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${status.open ? 'bg-sage' : 'bg-ink/30'}`} data-id="element-7557" data-mp="1lmj1hrgaoiky" />
            {status.label}
          </p>
          <h2 id="home-visit" className={`mt-5 ${sectionHeading} text-ink`} data-id="element-7558" data-mp="1bobw85vdboys">
            <HeadingText heading={copy.heading} accentBlock data-id="element-7559" data-mp="17n9wlnxjhawa" />
          </h2>
          {copy.body && <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7560" data-mp="17pm9vnscqx30">{fill(copy.body)}</p>}

          <address className="mt-8 flex max-w-lg gap-3 border-t border-ink/15 pt-6 text-[15px] not-italic leading-relaxed text-ink" data-id="element-7561" data-mp="12pzzrn7tlwyf">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-7562" data-mp="sxltqi9pm46g" />
            <span data-id="element-7563" data-mp="o0264rr4gf1u">
              {salon.street}, {salon.postalCode} {salon.locality}
              <span className="block text-muted" data-id="element-7564" data-mp="jy72vhpmrrlf">
                Alleen op afspraak{closed ? ` · ${closed.toLowerCase()}` : ''}
              </span>
            </span>
          </address>

          {/* Eén knop: dit is het slot van de pagina. Route en praktische info als rustige links. */}
          {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="mt-8 inline-flex min-h-[52px] w-full items-center justify-center rounded-full bg-ink px-7 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98] sm:w-auto" data-id="element-7565" data-mp="2vjzk30c4dqpa">
              {fill(copy.cta)}
            </button>}
          <div className="mt-4 flex flex-wrap items-center gap-x-6" data-id="element-7566" data-mp="2bup21fowito0">
            {copy.secondaryCta && <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7567" data-mp="2lmuvyuyprl5u">
                {fill(copy.secondaryCta)}
                <span className="sr-only" data-id="element-7568" data-mp="1o6pc2hzez7ah"> {shopLabels.newTab}</span>
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7569" data-mp="1t623zh5574jx" />
              </a>}
            <Link to="/contact" className="inline-flex min-h-[44px] items-center text-[14px] text-muted underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7570" data-mp="2wg6f4y6i5f6i">
              Openingstijden, parkeren en aanbellen
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}